import moment, { Moment, unitOfTime } from 'moment';
import momentTimeZone from 'moment-timezone';

// import 'moment/locale/en';
import 'moment/locale/nl';
import 'moment/locale/de';
import 'moment/locale/fr';
import 'moment/locale/it';
import 'moment/locale/es';
import 'moment/locale/pt';
import 'moment/locale/ja';
import 'moment/locale/ko';
import 'moment/locale/zh-cn';
import 'moment/locale/uk';
import 'moment/locale/hi';
import 'moment/locale/tr';
import 'moment/locale/pl';
import 'moment/locale/cs';
import 'moment/locale/ro';
import 'moment/locale/hu';
import 'moment/locale/da';
import 'moment/locale/sv';
import 'moment/locale/nb';
import 'moment/locale/fi';

import i18n from 'i18n-js';

import { languagePrefixes } from './translations';

export const DATE_FORMAT_DISPLAY = 'DD-MMM-YYYY';
export const DATE_TIME_FORMAT_DISPLAY = 'DD-MMM-YYYY HH:mm';

export const convertTime = (time: number, fullHours?: boolean) => {
  let hors: number | string = Math.floor(time / 60 / 60);
  let min: number | string = Math.floor((time - (hors as number) * 60 * 60) / 60);
  let sec: number | string = time - (hors as number) * 60 * 60 - (min as number) * 60;
  if (hors < 10 && fullHours) {
    hors = `0${hors}`;
  }
  if (min < 10) {
    min = `0${min}`;
  }
  if (sec < 10) {
    sec = `0${sec}`;
  }
  return `${hors}:${min}:${sec}`;
};

export const formatHours = (time: string) => {
  if (time[0] === '0') {
    return time.slice(1);
  }
  return time;
};

export const timeToSeconds = (t?: string | Date) => {
  const base = t ? new Date(t) : new Date();
  return Math.round(base.getTime() / 1000);
};

let tz = '';
let locale = '';

export const setTZ = (t: string) => (tz = t);
export const setLocale = (t: string) => {
  let l = t;
  languagePrefixes.forEach((prefix) => {
    if (t.includes(prefix as string)) {
      l = t.replace(`-${prefix}`, '');
    }
  });
  const locales = moment.locales();
  if (locales.includes(l)) {
    moment.locale(l);
    locale = l;
  }
};

export const getLocale = () => locale;

export const formatSinceTime = (t: string, is24HourFormat = false, absoluteSinceTime = false, withoutTranslation = false) => {
  const time = formatTimeDateToTimeZone(t, is24HourFormat);
  if (time === 'Invalid date' || !t) {
    return t ? t : '';
  }
  const dayGone = localTime(t).startOf('day').diff(localTime().startOf('day'), 'days');
  const secGone = localTime().startOf('second').diff(localTime(t).startOf('second'), 'seconds');

  if (dayGone === 0) {
    if (absoluteSinceTime && secGone < 3600) {
      return secGone < 60
        ? `${i18n.t('Just now')}`
        : secGone < 120
        ? `${i18n.t('1 minute ago')}`
        : `${Math.trunc(secGone / 60)} ${i18n.t('minutes ago')}`;
    }
    return withoutTranslation ? `Today ${time}` : `${i18n.t('Today')} ${time}`;
  }
  if (dayGone === 1) {
    return `${i18n.t('Tomorrow')} ${time}`;
  }

  if (dayGone === -1) {
    return withoutTranslation ? `${i18n.t('Yesterday')} ${time}` : `${i18n.t('Yesterday')} ${time}`;
  }

  if (dayGone >= -6 && dayGone < -1) {
    return `${i18n.t(localTime(t).local().format('dddd'))} ${formatTimeDateToTimeZone(t, is24HourFormat)}`;
  }

  return `${localTime(t).local().format('ll')} ${time}`;
};

export const formatSinceDate = (d: string | Date | number) => {
  const date = localTime(d).format('DD MMM');

  const dayGone = localTime(d).startOf('day').diff(localTime().startOf('day'), 'days');

  if (dayGone === 0) {
    return `${i18n.t('Today')} ${date}`;
  }

  if (dayGone === -1) {
    return `${i18n.t('Yesterday')} ${date}`;
  }

  if (dayGone < -1) {
    return localTime(d).format('ddd DD MMM');
  }

  return date;
};

export const formatSinceTimeWithDayTranslationCase = (t: string, is24HourFormat = false) => {
  const sinceTime = formatSinceTime(t, is24HourFormat, false, true);
  const removeWords = [i18n.t('Yesterday'), i18n.t('Today'), 'Yesterday', 'Today'];

  const timePart = removeWords.reduce((result, word) => result.replace(word, ''), sinceTime).trim();

  if (sinceTime.includes(i18n.t('Yesterday')) || sinceTime.includes('Yesterday')) {
    return `${i18n.t('Since Yesterday')} ${timePart}`;
  }

  if (sinceTime.includes(i18n.t('Today')) || sinceTime.includes('Today')) {
    return `${i18n.t('Since Today')} ${timePart}`;
  }

  return `${i18n.t('Since')} ${sinceTime}`;
};

// Accepts the assorted date-like values various pickers/libraries hand back
// (dayjs objects via `.$d`, JS Dates, ISO strings, moment) and returns a
// `Moment` (or `null` if falsy). Useful when state can hold any of those.
export const toMoment = (v: unknown): Moment | null => {
  if (!v) return null;
  if (typeof v === 'object' && v !== null && '$d' in v) {
    return moment((v as { $d: Date }).$d);
  }
  return moment(v as Parameters<typeof moment>[0]);
};

export const localTime = (t?: string | Date | number | Moment) => {
  if (tz) {
    return momentTimeZone.utc(t).tz(tz);
  }
  return moment.utc(t).local();
};

export const addTimeZoneToLocalTime = (t: string | Date | number) => {
  if (tz) {
    return moment.tz(t, tz);
  }
  return moment.utc(t).local();
};

export const isSameDay = (date: string | Date | number | Moment) => {
  return localTime(date).isSame(localTime(), 'day');
};

export const formatTableTime = (t: string | Date | number, includeYear?: boolean) => {
  if (!t) return '';
  return `${localTime(t).local().format('DD')}-${i18n.t(localTime(t).local().format('MMM'))}${
    includeYear ? '-' + localTime(t).local().format('YYYY') : ''
  }`;
};

const timeZones12Hours = [
  'Asia/Manila',
  'Europe/London',
  'Europe/Dublin',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Brisbane',
  'Australia/Adelaide',
  'Australia/Perth',
  'Australia/Hobart',
  'Pacific/Auckland',
  'Atlantic/Bermuda',
  'Asia/Kolkata',
  'Asia/Karachi',
  'Asia/Dhaka',
  'Asia/Colombo',
  'Indian/Maldives',
  'Asia/Kathmandu',
  'Asia/Thimphu',
  'Asia/Singapore',
  'Asia/Kuala_Lumpur',
  'Asia/Hong_Kong',
  'Asia/Macau',
  'Asia/Taipei',
  'Asia/Seoul',
];

export const formatTimeDateToTimeZone = (date: string | Date | number, is24HourFormat?: boolean) => {
  if (tz) {
    const is12HourFormat = tz.includes('America') || timeZones12Hours.includes(tz);
    return momentTimeZone
      .utc(date)
      .tz(tz)
      .format(is12HourFormat ? 'h:mm A' : 'HH:mm');
  }
  return moment
    .utc(date)
    .local()
    .format(is24HourFormat ? 'HH:mm' : 'h:mm A');
};

export const convertTimeLetter = (t: number, isMiliseconds = false, toNumber = false, includeDays = false) => {
  let time = t;

  if (!time) {
    return { d: '0', h: '0', m: '00', s: '00' };
  }

  if (includeDays) {
    if (isMiliseconds) {
      time = Math.floor(time / 1000);
    }

    const days = Math.floor(time / 60 / 60 / 24);
    const hours = Math.floor((time % (60 * 60 * 24)) / 60 / 60);
    let min: number | string = Math.floor((time % (60 * 60)) / 60);
    let sec: number | string = Math.floor(time % 60);

    if (min < 10 && !toNumber) {
      min = `0${min}`;
    }
    if (sec < 10 && !toNumber) {
      sec = `0${sec}`;
    }

    const data: Record<string, number | string> = { d: days, h: hours, m: min, s: sec };

    if (isMiliseconds) {
      data.ms = Math.round((t / 1000 - time) * 1000);
    }

    return data;
  } else {
    if (isMiliseconds) {
      time = Math.floor(time / 1000);
    }

    const hours = Math.floor(time / 60 / 60);
    let min: number | string = Math.floor((time % (60 * 60)) / 60);
    let sec: number | string = Math.floor(time % 60);

    if (min < 10 && !toNumber) {
      min = `0${min}`;
    }
    if (sec < 10 && !toNumber) {
      sec = `0${sec}`;
    }

    const data: Record<string, number | string> = { d: 0, h: hours, m: min, s: sec };

    if (isMiliseconds) {
      data.ms = Math.round((t / 1000 - time) * 1000);
    }

    return data;
  }
};

export const dateToSeconds = ({ h, m, s }: { h: number | string; m: number | string; s: number | string }) => {
  return (Number(h) * 60 + Number(m)) * 60 + Number(s);
};

export const formatTimeEvent = (t: Record<string, number | string>, includeMilisecons = true, includeSecondsAlways = false) => {
  const h = Number(t.h);
  const m = Number(t.m);
  const s = Number(t.s);
  const ms = Number(t.ms || 0);

  if (h > 0) {
    return `${h}${i18n.t('h')} ${m > 0 ? `${m}${i18n.t('m')}` : ''} ${includeSecondsAlways ? `${s}${i18n.t('s')}` : ''}`;
  }

  if (m > 0) {
    return `${m}${i18n.t('m')} ${s > 0 ? `${s}${i18n.t('s')}` : ''}`;
  }

  if (s > 0 && s < 10) {
    if (ms > 100 && includeMilisecons) {
      return `${s}.${String(ms).charAt(0)}${i18n.t('s')}`;
    }
    return `${s}${i18n.t('s')}`;
  }

  if (s > 0) {
    return `${s}${i18n.t('s')}`;
  }

  if (includeMilisecons) {
    if (ms === 0) {
      return '';
    }

    return `${ms}${i18n.t('ms')}`;
  } else {
    return `${s}${i18n.t('s')}`;
  }
};

export const formatDateToLocalDateTime = (t: string | Date | number, is24HourFormat?: boolean, includeYear = true) => {
  const lclTime = localTime(t);
  return `${formatTableTime(lclTime, includeYear)} ${formatTimeDateToTimeZone(lclTime, is24HourFormat)}`;
};

export const formatStartOf = (v: unitOfTime.StartOf) => (v === 'week' ? 'isoWeek' : v);

export const renderTimeRange = (start: string | Date | number, end?: string | Date | number) => {
  const isSameDate = localTime(start).startOf('day').isSame(localTime(end).startOf('day'));
  const isSameYearAndThisYear = end
    ? localTime(start).startOf('year').isSame(localTime(end).startOf('year')) && localTime(start).startOf('year').isSame(localTime().startOf('year'))
    : false;

  const formatStart = (() => {
    const isStartToday = localTime(start).startOf('day').format('YYYY-MM-DD') === localTime().startOf('day').format('YYYY-MM-DD');
    if (isStartToday) {
      return i18n.t('Today');
    }
    const isStartYesterday =
      localTime(start).startOf('day').format('YYYY-MM-DD') === localTime().subtract(1, 'day').startOf('day').format('YYYY-MM-DD');
    if (isStartYesterday) {
      return i18n.t('Yesterday');
    }

    if (isSameYearAndThisYear) {
      return localTime(start).format('DD MMM');
    }

    return localTime(start).format('DD MMM YYYY');
  })();

  if (!end) {
    const renderStart = [formatStart, localTime(start).format('HH:mm')];
    return `${renderStart.join(' ')}`;
  }

  const formatEnd = (() => {
    if (isSameDate) {
      return null;
    }

    const isEndToday = localTime(end).startOf('day').format('YYYY-MM-DD') === localTime().startOf('day').format('YYYY-MM-DD');
    if (isEndToday) {
      return i18n.t('Today');
    }

    const isEndYesterday = localTime(end).startOf('day').format('YYYY-MM-DD') === localTime().subtract(1, 'day').startOf('day').format('YYYY-MM-DD');
    if (isEndYesterday) {
      return i18n.t('Yesterday');
    }

    if (isSameYearAndThisYear) {
      return localTime(start).format('DD MMM');
    }

    return localTime(start).format('DD MMM YYYY');
  })();

  const renderStart = [formatStart, localTime(start).format('HH:mm')];
  const renderEnd = [localTime(end).format('HH:mm')];
  if (formatEnd) {
    renderEnd.unshift(formatEnd);
  }

  return `${renderStart.join(' ')} - ${renderEnd.join(' ')}`;
};

export const calculateTimeDifferenceInPercentage = (time: number | null | undefined, targetTime: number) => {
  return time != null && !!targetTime ? `${Math.round(Math.abs(1 - time / targetTime) * 100)}%` : null;
};

export const getUTCFormatOfLocalTime = (time: string | Date | number) => localTime(time).utc().format();

export const formatToDateTime = ({ t, is24HourFormat = true }: { t: string | Date | number; is24HourFormat?: boolean }) => {
  const hoursPart = is24HourFormat ? 'HH' : 'h';
  return localTime(t).format(`D-MMM-YYYY ${hoursPart}:mm:ss`);
};

const calculateTimeSeconds = (str: string) => {
  // H:MM:SS or M:SS or H:MM
  const colonMatch = str.match(/^(\d+):(\d{1,2})(?::(\d{1,2}))?$/);
  if (colonMatch) {
    const [, a, b, c] = colonMatch;
    if (c !== undefined) return parseInt(a) * 3600 + parseInt(b) * 60 + parseInt(c);
    return parseInt(a) * 3600 + parseInt(b) * 60;
  }

  // Dutch notation: 1u30, 2u, 1u30m
  const dutchMatch = str.match(/^(\d+(?:\.\d+)?)\s*u\s*(\d+(?:\.\d+)?)?\s*m?\s*$/);
  if (dutchMatch) {
    const hours = parseFloat(dutchMatch[1]);
    const mins = dutchMatch[2] ? parseFloat(dutchMatch[2]) : 0;
    return hours * 3600 + mins * 60;
  }

  // Explicit units: 1h30m15s, 1h30m, 1h, 30m, 90s, 1.5h
  const unitMatch = str.match(/^(?:(\d+(?:\.\d+)?)\s*h)?\s*(?:(\d+(?:\.\d+)?)\s*m(?:in)?)?\s*(?:(\d+(?:\.\d+)?)\s*s(?:ec)?)?\s*$/);
  if (unitMatch && (unitMatch[1] || unitMatch[2] || unitMatch[3])) {
    const h = parseFloat(unitMatch[1] || '0');
    const m = parseFloat(unitMatch[2] || '0');
    const s = parseFloat(unitMatch[3] || '0');
    return h * 3600 + m * 60 + s;
  }

  // Pure number: interpret as seconds
  const num = parseFloat(str);
  if (!isNaN(num)) return num;

  return 0;
};

export const parseTimeToSeconds = (input: string | null | undefined, decimalSeparator = '.') => {
  if (!input || typeof input !== 'string') return 0;
  let str = input.trim().toLowerCase();
  if (!str) return 0;

  if (decimalSeparator === ',') {
    str = str.replace(/(\d),(\d)/g, '$1.$2');
  }

  // Handle addition/subtraction: "1h + 30m", "1h30m + 15s"
  if (/[+\-]/.test(str.replace(/[esm]\+/g, ''))) {
    const parts = str.split(/\s*([+\-])\s*/);
    let total = 0;
    let op = '+';
    for (const part of parts) {
      if (part === '+' || part === '-') {
        op = part;
        continue;
      }
      const val = calculateTimeSeconds(part);
      total = op === '+' ? total + val : total - val;
    }
    return Math.max(0, Math.round(total));
  }

  return Math.max(0, Math.round(calculateTimeSeconds(str)));
};

export const formatSecondsToTime = (totalSeconds: number) => {
  if (!totalSeconds || totalSeconds <= 0) return '0:00:00';
  const s = Math.round(totalSeconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

/**
 * Validates if the input string represents a valid time format.
 * Supported formats:
 * - h:mm:ss or hh:mm:ss (e.g., "1:30:45", "01:30:45")
 * - h:mm or hh:mm (e.g., "1:30", "01:30")
 * - Xh Ym Zs format (e.g., "12h 25m 23s", "1h 30m", "45s")
 */
export const isValidTimeInput = (input: string | null | undefined) => {
  if (!input || typeof input !== 'string') return false;
  const trimmed = input.trim().toLowerCase();
  if (!trimmed) return false;

  // Format: h:mm:ss or hh:mm:ss or h:mm or hh:mm
  const colonFormat = /^\d{1,2}(:\d{2}){1,2}$/;
  if (colonFormat.test(trimmed)) return true;

  // Format: Xh Ym Zs (any combination, at least one unit)
  const unitFormat = /^(\d+h)?\s*(\d+m)?\s*(\d+s)?$/;
  if (unitFormat.test(trimmed) && /\d/.test(trimmed)) return true;

  return false;
};
